import { ApiError, NetworkError } from '../lib/api'
import type { DraftStore, LocalDraft } from '../lib/draft-storage'
import type { LogDraft } from './drafts-api'

export type Values = Record<string, unknown>

/** One log a draft becomes: the body for the create endpoint of `kind`. */
export type CommitEntry = { kind: string; entry: Values }

/** The draft endpoints for the farm the entry is on. */
export interface DraftServer {
  get(id: string): Promise<LogDraft>
  create(body: { kind: string; step: number; payload: Values }): Promise<LogDraft>
  update(id: string, body: { step: number; payload: Values }): Promise<unknown>
  commit(id: string, body: { entries: CommitEntry[] }): Promise<unknown>
}

export interface Timers {
  set(fn: () => void, ms: number): unknown
  clear(handle: unknown): void
}

export const browserTimers: Timers = { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) }

/**
 * A failure that says nothing about the request: no network, or the server is down. Worth retrying
 * later. An answer (403, 409, 422) is not, and neither is an error in our own code: the grower
 * needs to see those, not be told to wait for a connection.
 */
export const isTransient = (err: unknown) => err instanceof NetworkError || (err instanceof ApiError && err.status >= 500)

export class AlreadyRecordedError extends Error {
  constructor() {
    super('This entry has already been recorded.')
  }
}

const SYNC_DELAY_MS = 1000

/**
 * The wizard's in-progress entry, without React. Every change is written to this browser at once
 * and sent to the server a moment later (and again when the connection returns).
 *
 * Until the first step is done there is no server draft; `ensureCreated` makes one. If that fails
 * for want of a network, the entry is kept in this browser under a "new" key for its kind and
 * creation is retried on the next change or when the browser is back online, so nothing typed
 * offline is lost. When a draft is reopened, unsent local edits win over the server copy.
 */
export class DraftSync {
  id: string | null
  latest: LocalDraft
  private timer: unknown = null
  private creating: Promise<string> | null = null
  /** The first step is done: from here on the entry is worth a server draft. */
  private wantsServerCopy: boolean
  private syncing = false

  constructor(
    private readonly kind: string,
    private readonly server: DraftServer,
    private readonly store: DraftStore,
    private readonly timers: Timers = browserTimers,
    initial: { id: string | null; step: number; values: Values },
    private readonly delayMs = SYNC_DELAY_MS,
  ) {
    this.id = initial.id
    this.wantsServerCopy = initial.id !== null
    this.latest = { step: initial.step, values: initial.values, dirty: false }
  }

  /** Where this browser keeps the entry: by draft id, or by kind until the server has one. */
  get pendingKey() {
    return `new:${this.kind}`
  }
  private get key() {
    return this.id ?? this.pendingKey
  }

  /** An entry of this kind started but never sent to the server (typed offline), if any. */
  pending(): LocalDraft | null {
    return this.store.read(this.pendingKey)
  }

  /**
   * Opens a server draft. Unsent local edits win; a committed draft can't be reopened. With no
   * network, the local copy is used if there is one.
   */
  async load(id: string): Promise<LocalDraft> {
    const local = this.store.read(id)
    let server: LogDraft | null = null
    try {
      server = await this.server.get(id)
    } catch (err) {
      if (!local || !isTransient(err)) throw err
    }
    if (server?.committedAt) {
      this.store.clear(id)
      throw new AlreadyRecordedError()
    }
    this.id = id
    this.wantsServerCopy = true
    this.latest = local && (local.dirty || !server) ? local : { step: server!.step, values: server!.payload, dirty: false }
    if (this.latest.dirty) this.schedule(0)
    return this.latest
  }

  /** Takes back an entry typed offline before it reached the server (see `pending`). */
  resumePending(local: LocalDraft) {
    this.latest = { ...local, dirty: true }
    this.wantsServerCopy = true
    this.schedule(0)
  }

  touch(next: { step?: number; values?: Values }) {
    this.latest = { step: next.step ?? this.latest.step, values: next.values ?? this.latest.values, dirty: true }
    // Before the first step is done there is nothing worth keeping yet.
    if (this.id || this.wantsServerCopy) this.store.write(this.key, this.latest)
    this.schedule(this.delayMs)
  }

  /** Sends what is pending now: creates the server draft if it is still missing, else updates it. */
  async flush(): Promise<void> {
    if (!this.id) {
      if (!this.wantsServerCopy) return
      try {
        await this.ensureCreated()
      } catch {
        // Offline or refused: kept locally; retried on the next change or when back online.
      }
      return
    }
    await this.sync()
  }

  /** Creates the server draft the first time it is needed and returns its id. */
  ensureCreated(): Promise<string> {
    this.wantsServerCopy = true
    if (this.id) return Promise.resolve(this.id)
    if (!this.creating) {
      const sent = this.latest
      this.store.write(this.pendingKey, sent)
      this.creating = this.server
        .create({ kind: this.kind, step: sent.step, payload: sent.values })
        .then((draft) => {
          this.id = draft.id
          this.store.clear(this.pendingKey)
          // Changes made while the request was in flight still need sending.
          this.latest = this.latest === sent ? { ...sent, dirty: false } : this.latest
          this.store.write(draft.id, this.latest)
          if (this.latest.dirty) this.schedule(this.delayMs)
          return draft.id
        })
        .finally(() => {
          this.creating = null
        })
    }
    return this.creating
  }

  private async sync() {
    const id = this.id
    if (!id || this.syncing || !this.latest.dirty) return
    this.syncing = true
    const sent = this.latest
    try {
      await this.server.update(id, { step: sent.step, payload: sent.values })
      if (this.latest === sent) {
        this.latest = { ...sent, dirty: false }
        this.store.write(id, this.latest)
      }
    } catch {
      // Stay dirty: retried on the next change or when the browser is back online.
    } finally {
      this.syncing = false
    }
    if (this.latest.dirty && this.latest !== sent && this.id === id) this.schedule(this.delayMs)
  }

  /**
   * Records the entry: creates the server draft if needed, then commits it, which creates its logs
   * (one per field, say) and retires the draft together. Safe to call again after a failure: the
   * server returns the logs already made rather than recording them twice.
   */
  async commit(entries: CommitEntry[]): Promise<void> {
    const id = await this.ensureCreated()
    this.cancel()
    await this.server.commit(id, { entries })
    this.id = null
    this.wantsServerCopy = false
    this.store.clear(id)
    this.store.clear(this.pendingKey)
  }

  /** Forgets the entry in this browser (after a discard). */
  forget() {
    this.cancel()
    if (this.id) this.store.clear(this.id)
    this.store.clear(this.pendingKey)
  }

  cancel() {
    if (this.timer !== null) this.timers.clear(this.timer)
    this.timer = null
  }

  private schedule(ms: number) {
    this.cancel()
    this.timer = this.timers.set(() => {
      this.timer = null
      void this.flush()
    }, ms)
  }
}
